import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, HostBinding, OnChanges, SimpleChanges } from '@angular/core';

export enum StepState {
  Pending = 'pending',
  Current = 'current',
  Complete = 'complete',
  Partial = 'partial'
}

export enum StepperStyle {
  Streamlined = 'streamlined',
  Traditional = 'traditional'
}

export interface StepConfig {
  label?: string;
  state: StepState;
}

@Component({
  selector: 'cs-stepper',
  templateUrl: 'cs-stepper.html',
  host: { 'class': 'stepper' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsStepper implements OnChanges {
  private static readonly MAX_VISIBLE_STEPS = 7;

  // Streamlined mode inputs (legacy)
  @Input() totalSteps: number = 0;
  @Input() currentStep: number = 0;

  // Traditional labeled mode inputs
  @Input() steps: StepConfig[] = [];
  @Input() style: StepperStyle = StepperStyle.Streamlined;

  // Click event for step navigation
  @Output() stepClick = new EventEmitter<number>();

  // windowing for Traditional mode when steps > MAX_VISIBLE_STEPS
  windowStart = 0;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['steps'] && this.style === StepperStyle.Traditional) {
      this.adjustWindowToCurrentStep();
    }
  }

  onStepClick(visibleIndex: number) {
    this.stepClick.emit(this.windowStart + visibleIndex);
  }

  @HostBinding('class.traditional') get isTraditional() {
    return this.style === StepperStyle.Traditional;
  }

  @HostBinding('class.streamlined') get isStreamlined() {
    return this.style === StepperStyle.Streamlined;
  }

  // for streamlined mode
  get streamlinedSteps(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  isStepCompleted(stepNumber: number): boolean {
    return stepNumber <= this.currentStep;
  }

  // Traditional mode: visible window of steps
  get visibleSteps(): StepConfig[] {
    if (this.steps.length <= CsStepper.MAX_VISIBLE_STEPS) {
      return this.steps;
    }
    return this.steps.slice(this.windowStart, this.windowStart + CsStepper.MAX_VISIBLE_STEPS);
  }

  get hasMoreBefore(): boolean {
    return this.windowStart > 0;
  }

  get hasMoreAfter(): boolean {
    return this.windowStart + CsStepper.MAX_VISIBLE_STEPS < this.steps.length;
  }

  // for connector lines between steps
  getConnectorState(visibleIndex: number): 'complete' | 'pending' {
    const realIndex = this.windowStart + visibleIndex;
    const left = this.steps[realIndex];
    const right = this.steps[realIndex + 1];

    // blue only between completed days, or between completed day and current day
    return left.state === StepState.Complete &&
           (right?.state === StepState.Complete || right?.state === StepState.Current)
           ? 'complete' : 'pending';
  }

  private adjustWindowToCurrentStep() {
    const max = CsStepper.MAX_VISIBLE_STEPS;
    if (this.steps.length <= max) {
      this.windowStart = 0;
      return;
    }

    const currentIndex = this.steps.findIndex(s => s.state === StepState.Current);
    if (currentIndex === -1) return;

    // if current step is outside the visible window, jump to the page containing it
    if (currentIndex < this.windowStart || currentIndex >= this.windowStart + max) {
      this.windowStart = Math.floor(currentIndex / max) * max;
    }
  }
}
